import React, { useEffect, useState } from 'react'
import type { ChangePasswordRequest, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { clip } from '../../../utils/inputs'
import type { FormMessage } from '../common/my-account-common'

export interface MyAccountPasswordProps {
    open: boolean
    onClose: () => void
    onChanged: (message: string) => void
}

export interface MyAccountPasswordState {
    oldPassword: string
    newPassword: string
    newPassword2: string
}

const PASSWORD_MIN = 8
const PASSWORD_MAX = 50
const SAVE_ERROR = 'No se pudo cambiar la contraseña'

const emptyState: MyAccountPasswordState = {
    oldPassword: '',
    newPassword: '',
    newPassword2: '',
}

const toRequest = (state: MyAccountPasswordState): ChangePasswordRequest => ({
    oldPassword: state.oldPassword,
    newPassword: state.newPassword,
})

export const useMyAccountPasswordModel = ({ open, onClose, onChanged }: MyAccountPasswordProps) => {
    const [state, setState] = useState<MyAccountPasswordState>(emptyState)
    const [showPassword, setShowPassword] = useState(false)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)

    useEffect(() => {
        if (!open) return
        setState(emptyState)
        setShowPassword(false)
        setMessage(null)
    }, [open])

    const patch = (partial: Partial<MyAccountPasswordState>) => setState((current) => ({ ...current, ...partial }))

    const change = {
        oldPassword: (value: string) => patch({ oldPassword: clip(value, PASSWORD_MAX) }),
        newPassword: (value: string) => patch({ newPassword: clip(value, PASSWORD_MAX) }),
        newPassword2: (value: string) => patch({ newPassword2: clip(value, PASSWORD_MAX) }),
    }

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (state.newPassword !== state.newPassword2) {
            setMessage({ type: 'error', message: 'Las dos contraseñas no coinciden' })
            return
        }
        if (state.newPassword.length < PASSWORD_MIN || state.newPassword.length > PASSWORD_MAX) {
            setMessage({ type: 'error', message: `La nueva contraseña debe contener entre ${PASSWORD_MIN} y ${PASSWORD_MAX} caracteres` })
            return
        }
        try {
            setSaving(true)
            setMessage(null)
            const response: WrapperResponse<null> = await (await APIInvoke.invokePUT('/usuarios/password', toRequest(state))).json()
            if (response.ok) onChanged(response.message || 'Contraseña actualizada correctamente')
            else setMessage({ type: 'error', message: response.message || SAVE_ERROR })
        } catch (error) {
            console.error('error al cambiar la contraseña', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setSaving(false)
        }
    }

    return {
        form: state,
        open,
        saving,
        message,
        showPassword,
        inputType: showPassword ? 'text' : 'password',
        change,
        submit,
        close: () => { if (!saving) onClose() },
        toggleShowPassword: () => setShowPassword((value) => !value),
    }
}
